import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  Typography,
  Tag,
  Descriptions,
  Alert,
  Spin,
  Button,
  Tooltip,
  Input,
  InputRef,
  Modal,
  theme,
} from 'antd';
import { QrcodeOutlined } from '@ant-design/icons';
import EntryPacket from 'routes/app/arrivePacket';
import QRScanner from 'components/QRScanner';
import {
  useGetCollectionDetail,
  notifyCollectionArrival,
  useFulfillCollectionStore,
} from 'services/CollectionsService';
import { useDebounce } from 'util/Hooks';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { authTokenSelector } from 'redux/auth/selectors';
import CollectionV2Intake from './CollectionV2Intake';

const { Title, Text } = Typography;

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending_collection: { label: 'Pendiente', color: 'orange' },
  collected: { label: 'Colectada', color: 'blue' },
  received: { label: 'Ingresada al deposito', color: 'green' },
};

type ParsedQR =
  | { kind: 'legacy'; collectionId: number; storeId: number }
  | { kind: 'v2'; collectionId: number };

const parseQRCode = (raw: string): ParsedQR | null => {
  const value = raw.trim();
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length < 2) {
    const collectionId = Number(value);
    if (!Number.isSafeInteger(collectionId) || collectionId <= 0) return null;
    return { kind: 'v2', collectionId };
  }
  const storeId = Number(parts[parts.length - 1]);
  const collectionId = Number(parts.slice(0, parts.length - 1).join('-'));
  if (Number.isNaN(collectionId) || Number.isNaN(storeId)) return null;
  return { kind: 'legacy', collectionId, storeId };
};

const CollectionIngreso = () => {
  const authToken = useSelector(authTokenSelector);
  const { token } = theme.useToken();
  const { search } = useLocation();
  const [parsed, setParsed] = useState<ParsedQR | null>(() =>
    parseQRCode(new URLSearchParams(search).get('code') ?? ''),
  );
  const [showCamera, setShowCamera] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<InputRef>(null);
  const debouncedInput = useDebounce(inputValue, 1000);

  const [shipmentsAmount, setShipmentsAmount] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [errorMode, setErrorMode] = useState(false);
  const [errorNote, setErrorNote] = useState('');
  const { mutate: fulfill, isPending: isFulfilling } =
    useFulfillCollectionStore();

  const handleCloseModal = () => {
    setModalOpen(false);
    setErrorMode(false);
    setErrorNote('');
  };

  const {
    data: collection,
    isLoading,
    isError,
  } = useGetCollectionDetail(
    parsed?.kind === 'legacy' ? parsed.collectionId : null,
  );

  useEffect(() => {
    if (!parsed) {
      inputRef.current?.focus();
    }
  }, [parsed]);

  useEffect(() => {
    if (debouncedInput) {
      handleCode(debouncedInput);
      setInputValue('');
    }
  }, [debouncedInput]);

  const handleCode = (raw: string) => {
    setParseError(null);
    const result = parseQRCode(raw);
    if (!result) {
      setParseError(
        `Código inválido: "${raw}". Se espera {colecta} o {colecta}-{tienda}`,
      );
      return;
    }
    setParsed(result);
  };

  const handleScanQR = (data: string | null) => {
    if (data) {
      setShowCamera(false);
      handleCode(data);
    }
  };

  const handleReset = () => {
    setParsed(null);
    setParseError(null);
  };

  useEffect(() => {
    if (!parsed) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleReset();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [parsed]);

  const legacy = parsed?.kind === 'legacy' ? parsed : null;
  const store = collection?.stores?.find(
    (s) => s.storeId.toString() === legacy?.storeId.toString(),
  );
  const statusInfo = store
    ? (STATUS_LABELS[store.status] ?? {
        label: store.status,
        color: 'default',
      })
    : null;
  const isReceived = store?.status === 'received';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {!parsed && (
        <Card>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '48px 24px',
              gap: 24,
            }}
          >
            <QrcodeOutlined
              style={{ fontSize: 80, color: token.colorPrimary }}
              onClick={() => setShowCamera(true)}
            />
            <Title level={2} style={{ margin: 0, textAlign: 'center' }}>
              Ingresar colecta
            </Title>
            <Input
              ref={inputRef}
              size="large"
              value={inputValue}
              placeholder="ej: 42 o 42-7"
              style={{ width: 280, textAlign: 'center' }}
              onChange={(e) => setInputValue(e.target.value)}
              onPressEnter={() => {
                handleCode(inputValue);
                setInputValue('');
              }}
            />
            <Button type="link" onClick={() => setShowCamera(true)}>
              Escanear QR con la cámara
            </Button>
            {parseError && <Alert type="error" showIcon message={parseError} />}
          </div>
          <QRScanner
            onScan={handleScanQR}
            showCamera={showCamera}
            setShowCamera={setShowCamera}
          />
        </Card>
      )}

      {parsed?.kind === 'v2' && (
        <CollectionV2Intake
          collectionId={parsed.collectionId}
          onReset={handleReset}
          showBack
        />
      )}

      {parsed?.kind === 'legacy' && (
        <>
          {isLoading && (
            <Card>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: 40,
                }}
              >
                <Spin spinning />
              </div>
            </Card>
          )}
          {!isLoading && (isError || !collection) && (
            <Alert
              type="error"
              message={`No se encontró la colecta #${parsed.collectionId}`}
              action={
                <Button size="small" danger onClick={handleReset}>
                  Reintentar
                </Button>
              }
            />
          )}
          {collection && !store && (
            <Alert
              type="error"
              showIcon
              message={`Tienda #${legacy?.storeId} no encontrada en la colecta #${parsed.collectionId}`}
              action={
                <Button size="small" danger onClick={handleReset}>
                  Reintentar
                </Button>
              }
            />
          )}
          {collection && store && (
            <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
              <Card
                style={{ flex: 1 }}
                title={
                  <span>
                    Colecta #{collection.collectionRequestId} Tienda #
                    {legacy?.storeId}{' '}
                    {statusInfo && (
                      <Tag color={statusInfo.color}>{statusInfo.label}</Tag>
                    )}
                  </span>
                }
                extra={
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Tooltip title="Esc">
                      <Button danger onClick={handleReset}>
                        Cambiar colecta
                      </Button>
                    </Tooltip>
                    {!isReceived && (
                      <>
                        <Button
                          type="primary"
                          loading={isFulfilling}
                          onClick={() => setModalOpen(true)}
                        >
                          Marcar como recibida
                        </Button>
                        <Modal
                          open={modalOpen}
                          onCancel={handleCloseModal}
                          title="¿Marcar como recibida?"
                          footer={
                            <div
                              style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                              }}
                            >
                              <Button
                                type="link"
                                danger
                                style={{ padding: 0 }}
                                onClick={() => setErrorMode((v) => !v)}
                              >
                                Algo salió mal
                              </Button>
                              <div style={{ display: 'flex', gap: 8 }}>
                                <Button onClick={handleCloseModal}>
                                  Cancelar
                                </Button>
                                <Button
                                  type="primary"
                                  loading={isFulfilling}
                                  onClick={() => {
                                    fulfill({
                                      collectionId: parsed.collectionId,
                                      storeId: legacy?.storeId as number,
                                    });
                                    handleCloseModal();
                                  }}
                                >
                                  Confirmar
                                </Button>
                              </div>
                            </div>
                          }
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 12,
                            }}
                          >
                            <p style={{ margin: 0, color: '#595959' }}>
                              Se finalizará la colecta para esta tienda.
                            </p>
                            {errorMode && (
                              <>
                                <p
                                  style={{
                                    margin: 0,
                                    fontSize: 12,
                                    color: '#8c8c8c',
                                  }}
                                >
                                  ¿Qué pasó?
                                </p>
                                <textarea
                                  autoFocus
                                  value={errorNote}
                                  onChange={(e) => setErrorNote(e.target.value)}
                                  placeholder="Describí qué pasó..."
                                  rows={4}
                                  style={{
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    padding: '8px 12px',
                                    borderRadius: 6,
                                    border: '1px solid #d9d9d9',
                                    fontSize: 14,
                                    resize: 'vertical',
                                    fontFamily: 'inherit',
                                  }}
                                />
                              </>
                            )}
                          </div>
                        </Modal>
                      </>
                    )}
                  </div>
                }
              >
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Conductor">
                    {collection.driverName}
                  </Descriptions.Item>
                  <Descriptions.Item label="Placa">
                    {collection.vehiclePlate}
                  </Descriptions.Item>
                  {store && (
                    <>
                      <Descriptions.Item label="Tienda">
                        {store.storeName}
                      </Descriptions.Item>
                      <Descriptions.Item label="Dirección">
                        {store.storeAddress}
                      </Descriptions.Item>
                    </>
                  )}
                  {!store && (
                    <Descriptions.Item label="Tienda ID">
                      #{legacy?.storeId}
                    </Descriptions.Item>
                  )}
                  {collection.depositName && (
                    <Descriptions.Item label="Depósito">
                      {collection.depositName}
                    </Descriptions.Item>
                  )}
                </Descriptions>
              </Card>
              {store && (
                <Card style={{ minWidth: 220 }}>
                  <div style={{ textAlign: 'center', padding: '16px 0' }}>
                    <Text type="secondary" style={{ fontSize: 16 }}>
                      Ingresados
                    </Text>
                    <div>
                      <span
                        style={{
                          fontSize: 72,
                          fontWeight: 700,
                          lineHeight: 1.1,
                          color: '#0db8b2',
                        }}
                      >
                        {shipmentsAmount ?? store.linkedShipmentsAmount ?? 0}
                      </span>
                      <span
                        style={{
                          fontSize: 48,
                          fontWeight: 400,
                          color: '#8c8c8c',
                          margin: '0 8px',
                        }}
                      >
                        /
                      </span>
                      <span
                        style={{
                          fontSize: 72,
                          fontWeight: 700,
                          lineHeight: 1.1,
                          color: '#262626',
                        }}
                      >
                        {store.declaredPackageCount ?? '?'}
                      </span>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          )}
        </>
      )}

      {parsed?.kind === 'legacy' && !isLoading && isReceived && (
        <Alert
          type="warning"
          showIcon
          message="Colecta ya ingresada al depósito"
          description="No se pueden agregar más envíos a esta tienda."
        />
      )}
      {parsed?.kind === 'legacy' && !isLoading && store && !isReceived && (
        <EntryPacket
          onSuccess={async (scannedCode) => {
            const result = await notifyCollectionArrival(
              parsed.collectionId,
              legacy?.storeId as number,
              scannedCode,
              authToken,
            );
            if (result?.shipments_amount != null) {
              setShipmentsAmount(result.shipments_amount);
            }
          }}
        />
      )}
    </div>
  );
};

export default CollectionIngreso;
